import { ViewType } from 'generated/graphql';
import type { GetBaseViewerDataQuery } from 'generated/graphql';
import { parseLayoutString, fromAllowedViewportType } from './steppedHPTypes';
import type { StepConfiguration } from './steppedHPTypes';

export type HangingProtocol = GetBaseViewerDataQuery['matchHangingProtocolByWorklistsAndStudies'];

/**
 * Empty viewport configuration for cleared viewport slots in stepped HPs.
 * Cleared viewports are stripped during save, so they have no entry in step data.
 * This constant provides an explicit empty entry so the factory merge doesn't
 * leak stale user-persisted state into those positions.
 */
const CLEARED_VIEWPORT_CONFIGURATION = {
  viewType: ViewType.TwoDDre,
  study: null,
  series: null,
  stack: null,
  comparisonIndex: null,
  seriesDescriptions: [] as string[],
  seriesCriteriaSourceText: '',
  seriesCriteria: null,
} as const;

/**
 * Rebuilds a HangingProtocol object from a step configuration.
 *
 * Converts step layouts and viewport entries into the HangingProtocol shape
 * consumed by dehydrateHangingProtocol. The GQL server already resolves
 * study/series/stack SMIDs per-step via /hang-cases, so this function only
 * handles format conversion and empty-slot filling.
 *
 * Exported for testing.
 */
export function rebuildHPFromStep(
  originalHangingProtocol: HangingProtocol,
  stepConfig: StepConfiguration
): HangingProtocol {
  // Convert step layouts to HP format: { "0": "2,2" } -> [{ id: "0", layout: [2, 2] }]
  const stepLayoutWindowIds = new Set(Object.keys(stepConfig.layouts));
  const layouts = Object.entries(stepConfig.layouts).map(([id, layoutStr]) => {
    return { id, layout: parseLayoutString(layoutStr) as readonly [number, number] };
  });
  // Carry over layouts from the original HP for windows not defined by the step.
  // Steps sometimes only define viewer/0; without this, viewer/1 (secondary
  // monitor) would lose its layout on step switch and go blank.
  if (originalHangingProtocol?.data?.layouts) {
    for (const origLayout of originalHangingProtocol.data.layouts) {
      if (!stepLayoutWindowIds.has(origLayout.id)) {
        layouts.push({
          id: origLayout.id,
          layout: [origLayout.layout[0], origLayout.layout[1]] as readonly [number, number],
        });
      }
    }
  }

  // Convert viewport configurations to HP format
  const viewportsConfigurations = Object.entries(stepConfig.viewports).map(([id, config]) => {
    const displayName = config.display_name ?? '';
    return {
      id,
      configuration: {
        viewType: fromAllowedViewportType(config.view_type),
        study: config.study_smid ? { smid: config.study_smid } : null,
        series: config.series_smid ? { smid: config.series_smid } : null,
        stack: config.stack_smid ? { smid: config.stack_smid } : null,
        comparisonIndex: config.comparison_index ?? null,
        seriesDescriptions: displayName ? [displayName] : [],
        seriesCriteriaSourceText: config.series_criteria_source_text ?? displayName,
        seriesCriteria: null,
      },
    };
  });

  // Carry over viewport configs from the original HP for windows not defined
  // by the step, preserving viewer/1's assigned series during step navigation.
  if (originalHangingProtocol?.viewportsConfigurations) {
    for (const origVp of originalHangingProtocol.viewportsConfigurations) {
      const vpWindowId = origVp.id.split('-')[1];
      if (!stepLayoutWindowIds.has(vpWindowId) && !stepConfig.viewports[origVp.id]) {
        viewportsConfigurations.push({
          id: origVp.id,
          configuration: {
            viewType: origVp.configuration.viewType ?? ViewType.TwoDDre,
            study: origVp.configuration.study ?? null,
            series: origVp.configuration.series ?? null,
            stack: origVp.configuration.stack ?? null,
            comparisonIndex: origVp.configuration.comparisonIndex ?? null,
            seriesDescriptions: [...(origVp.configuration.seriesDescriptions ?? [])],
            seriesCriteriaSourceText: origVp.configuration.seriesCriteriaSourceText ?? '',
            seriesCriteria: null,
          },
        });
      }
    }
  }

  // Track viewport IDs that already have configurations to avoid duplicates.
  const existingViewportIds = new Set(viewportsConfigurations.map((vp) => vp.id));

  // Add explicit empty entries for viewport slots not present in step data.
  // Only process step-owned windows to avoid filling carried-over layouts.
  for (const { id: windowId, layout } of layouts) {
    if (!stepLayoutWindowIds.has(windowId)) continue;
    const [rows, cols] = layout;
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        const vpId = `Viewport-${windowId}-${r}-${c}`;
        if (!stepConfig.viewports[vpId] && !existingViewportIds.has(vpId)) {
          viewportsConfigurations.push({
            id: vpId,
            configuration: CLEARED_VIEWPORT_CONFIGURATION,
          });
          existingViewportIds.add(vpId);
        }
      }
    }
  }

  return {
    ...originalHangingProtocol,
    name: originalHangingProtocol?.name ?? '',
    data: {
      ...originalHangingProtocol?.data,
      layouts,
    },
    viewportsConfigurations,
  } as HangingProtocol;
}
